import { styled } from 'styled-system/jsx'

const PageStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
})

const Header = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const PageTitle = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '28px',
    fontWeight: '800',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.5px',
  },
})

const PageSubtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
  },
})

const Layout = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '16px',
    alignItems: 'stretch',
    minHeight: '480px',
    md: {
      gridTemplateColumns: '1fr',
    },
  },
})

const ThreadList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const ThreadItem = styled('button', {
  base: {
    textAlign: 'left',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    border: '1px solid',
    borderColor: '#EAE6F5',
    borderRadius: 'md',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    transition: 'border-color 150ms, background-color 150ms',
    _hover: {
      borderColor: 'quickpass.primarySoft',
    },
  },
  variants: {
    isSelected: {
      true: {
        borderColor: 'quickpass.primary',
        backgroundColor: '#F6F2FF',
      },
    },
  },
})

const ThreadTitle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
})

const ThreadPreview = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
})

const ThreadTime = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    color: 'quickpass.inkSoft',
  },
})

const ThreadUnread = styled('span', {
  base: {
    alignSelf: 'flex-start',
    minWidth: '18px',
    height: '18px',
    paddingX: '5px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primary',
    color: '#FFFFFF',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

const Conversation = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    border: '1px solid',
    borderColor: '#EAE6F5',
    borderRadius: 'lg',
    overflow: 'hidden',
    minHeight: '480px',
  },
})

const ConversationHeader = styled('div', {
  base: {
    padding: '14px 18px',
    borderBottom: '1px solid',
    borderColor: '#EFEBFA',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
})

const ConversationTitle = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '16px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const ConversationSubtitle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const MessagesScroll = styled('div', {
  base: {
    flex: '1',
    overflowY: 'auto',
    padding: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const MessageRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '78%',
  },
  variants: {
    direction: {
      out: { alignSelf: 'flex-end', alignItems: 'flex-end' },
      in: { alignSelf: 'flex-start', alignItems: 'flex-start' },
    },
  },
})

const MessageBubble = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    lineHeight: '1.45',
    padding: '10px 13px',
    borderRadius: 'md',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  variants: {
    direction: {
      out: {
        backgroundColor: 'quickpass.primary',
        color: '#FFFFFF',
        borderBottomRightRadius: '3px',
      },
      in: {
        backgroundColor: '#F1ECFF',
        color: 'quickpass.ink',
        borderBottomLeftRadius: '3px',
      },
    },
  },
})

const MessageMeta = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '10px',
    color: 'quickpass.inkSoft',
    marginTop: '3px',
    paddingX: '4px',
  },
})

const Composer = styled('form', {
  base: {
    display: 'flex',
    gap: '8px',
    padding: '12px',
    borderTop: '1px solid',
    borderColor: '#EFEBFA',
  },
})

const ComposerInput = styled('textarea', {
  base: {
    flex: '1',
    resize: 'none',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    border: '1px solid',
    borderColor: '#E0DAF2',
    borderRadius: 'md',
    padding: '10px 12px',
    minHeight: '44px',
    maxHeight: '120px',
    outline: 'none',
    _focus: {
      borderColor: 'quickpass.primary',
    },
  },
})

const EmptyState = styled('div', {
  base: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '40px',
    textAlign: 'center',
  },
})

const EmptyTitle = styled('p', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '0',
  },
})

const EmptyBody = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    maxWidth: '320px',
    lineHeight: '1.5',
  },
})

export {
  PageStack,
  Header,
  PageTitle,
  PageSubtitle,
  Layout,
  ThreadList,
  ThreadItem,
  ThreadTitle,
  ThreadPreview,
  ThreadTime,
  ThreadUnread,
  Conversation,
  ConversationHeader,
  ConversationTitle,
  ConversationSubtitle,
  MessagesScroll,
  MessageRow,
  MessageBubble,
  MessageMeta,
  Composer,
  ComposerInput,
  EmptyState,
  EmptyTitle,
  EmptyBody,
}
